import {
  ImageDetails,
  ImageGenerator,
  ImagesMap,
  ImageGenerationParams,
  UriData,
  UploadImagesDetails,
  PromptsData,
  UploadEventHandler,
  ImageGeneratorOptions,
} from './types';
import { CompStructure } from '@wix/document-services-types';
import {
  createAiMediaData,
  getGroupedMapByCropType,
  getImagesDataFromStructure,
  getImagesDescriptions,
  getLastNMapItems,
  getScenePromptId,
  getUploadImagesDataFromStructure,
} from './utils';
import { getImageFromServerless } from './fetchDataUtils';
import { PoolStorageManager } from '../poolStorage/poolStorageManager';
import { UploadFileFn } from '../types';
import { AiMediaData } from '@wix/editor-content-injector';
import {
  DEFAULT_IMAGE_ASPECT_RATIO,
  FLUX_PRO_ULTRA_IMAGE_GENERATION_PROMPT_ID,
  STRUCTURE_ID,
} from './consts';
import { ErrorReporter } from '@wix/editor-error-reporter';
import { PromptGeneratorQueuer } from './PromptGenerator';

export class AiImageGenerator implements ImageGenerator {
  private imagesStorageManager = new PoolStorageManager<ImageDetails>();
  private readonly promptGenerator = new PromptGeneratorQueuer();
  private uriData: UriData = new Map();

  constructor(private readonly options: ImageGeneratorOptions) {}

  private async createImagesFactory(
    imagesAmount: number,
    imageGenerationParams: ImageGenerationParams,
    sectionName: string,
    imagesMap: ImagesMap,
    groupName: string,
  ): Promise<ImageDetails[]> {
    const lastItems = getLastNMapItems(imagesMap, imagesAmount);
    const eventPayload = {
      amount: imagesAmount.toString(10),
      bunch: groupName,
    };

    imageGenerationParams?.onGenerationEvent?.({
      type: 'onBunchGenerationStart',
      payload: eventPayload,
    });

    const promptIds = await this.getPromptsIds(
      imageGenerationParams,
      sectionName,
      lastItems,
      groupName,
    );
    const newImages: ImageDetails[] = [];
    if (!promptIds) return [];

    await Promise.all(
      Object.entries(promptIds).map(async ([compId, promptKey]) => {
        const imageItem = lastItems.get(compId) as ImageDetails;
        const data = await this.generateImage(
          imageGenerationParams,
          compId,
          promptKey,
          imageItem.aspectRatio || DEFAULT_IMAGE_ASPECT_RATIO,
          FLUX_PRO_ULTRA_IMAGE_GENERATION_PROMPT_ID,
          groupName,
        );
        if (!data?.uri) return;
        newImages.push({ ...imageItem, localUri: data.uri, promptKey });
      }),
    );

    imageGenerationParams?.onGenerationEvent?.({
      type: 'onBunchGenerationSuccess',
      payload: eventPayload,
    });

    return newImages;
  }

  private getPromptsIds = async (
    imageGenerationParams: ImageGenerationParams,
    sectionName: string,
    imagesMap: ImagesMap,
    groupName: string,
  ): Promise<PromptsData | undefined> => {
    const scenePromptVer = getScenePromptId(sectionName, groupName);
    const structurePromptVer = STRUCTURE_ID;
    const imageDescriptions = getImagesDescriptions(imagesMap);
    const { siteDescription, businessTerm, msId, msInstance } =
      imageGenerationParams;

    try {
      const res = await this.promptGenerator.generate(
        {
          sectionName,
          scenePromptVer,
          structurePromptVer,
          siteDescription,
          businessTerm,
          msId,
          msInstance,
        },
        imageDescriptions,
        this.options.interactionStarted,
        this.options.interactionEnded,
      );

      return res;
    } catch (err) {
      console.error(err as string);
    }
  };

  private generateImage = async (
    imageGenerationParams: ImageGenerationParams,
    compId: string,
    promptKey: string,
    aspectRatio: string,
    imageGenerationPromptId: string,
    bunchName: string,
  ): Promise<{ uri: string } | undefined> => {
    const { msInstance } = imageGenerationParams;
    const eventPayload = {
      id: compId,
      bunch: bunchName,
      details: JSON.stringify({
        aspectRatio,
      }),
    };

    try {
      imageGenerationParams?.onGenerationEvent?.({
        type: 'onSingleGenerationStart',
        payload: eventPayload,
      });
      const image = await getImageFromServerless(
        msInstance,
        promptKey,
        aspectRatio,
        imageGenerationPromptId,
        compId,
      );
      if (!image) {
        console.error(`Failed to generate image for compId: ${compId}.`);
        return;
      }
      imageGenerationParams?.onGenerationEvent?.({
        type: 'onSingleGenerationSuccess',
        payload: eventPayload,
      });
      return { uri: image.url };
    } catch (error) {
      imageGenerationParams?.onGenerationEvent?.({
        type: 'onSingleGenerationError',
        payload: {
          ...eventPayload,
          errn: error?.toString() ?? '',
        },
      });
      throw new Error(
        `Failed to generate image for compId: ${compId}. Error: ${error}`,
      );
    }
  };

  public async getImages(
    sectionName: string,
    structure: CompStructure,
    imageGenerationParams: ImageGenerationParams,
  ): Promise<ImagesMap | undefined> {
    const imagesMap = getImagesDataFromStructure(structure);
    if (imagesMap.size === 0) {
      return;
    }

    const groupedMapByCropType = getGroupedMapByCropType(
      imagesMap,
      sectionName,
    );
    const images: ImagesMap = new Map();
    const promises: Promise<{
      groupName: string;
      generatedImages: Record<string, ImageDetails>;
    }>[] = [];

    for (const [groupName, cropTypeImagesMap] of groupedMapByCropType) {
      const storage =
        this.imagesStorageManager.getStorage(groupName) ??
        this.imagesStorageManager.createStorage(groupName);

      const promise = storage
        .create(
          Array.from(cropTypeImagesMap.keys()),
          async (amount: number) => {
            return this.createImagesFactory(
              amount,
              imageGenerationParams,
              sectionName,
              cropTypeImagesMap,
              groupName,
            );
          },
        )
        .then((generatedImages) => ({ groupName, generatedImages }));

      promises.push(promise);
    }
    const results = await Promise.all(promises);

    for (const { generatedImages } of results) {
      Object.entries(generatedImages).forEach(([compId, imageDetails]) => {
        images.set(compId, imageDetails);
        if (imageDetails.localUri) {
          this.uriData.set(compId, imageDetails.localUri);
        }
      });
    }

    return images;
  }

  public uploadImagesForStructure = async (
    uploadFileFn: UploadFileFn,
    sectionStructure: CompStructure,
    pageName: string,
    sectionTitle: string,
    tempMetaSiteInstance: string,
    eventsHanlder?: UploadEventHandler,
  ): Promise<AiMediaData> => {
    const imagesInStructure = await getUploadImagesDataFromStructure(
      sectionStructure,
      pageName,
      sectionTitle,
    );
    const filesToUpload = imagesInStructure.filter(([_, imageDetails]) =>
      imageDetails.hasOwnProperty('localUri'),
    );

    const eventPayload = {
      amount: filesToUpload.length.toString(10),
      bunch: `${pageName}_${sectionTitle}`,
    };

    eventsHanlder?.({ type: 'onBunchUploadStart', payload: eventPayload });

    const uploadPromises = filesToUpload.map(
      async ([compId, data]): Promise<UploadImagesDetails> => {
        let { staticUri } = data;
        if (staticUri) {
          return [compId, data];
        }

        const itemEventPayload = {
          ...eventPayload,
          compId,
        };

        eventsHanlder?.({
          type: 'onSingleUploadStart',
          payload: itemEventPayload,
        });

        const { localUri, displayName } = data;
        if (!localUri || !displayName) {
          throw new Error(
            `File doesn't exist yet for image with compId ${compId}`,
          );
        }

        try {
          staticUri = await uploadFileFn(
            { url: localUri, name: displayName },
            tempMetaSiteInstance,
          );
        } catch (error) {
          ErrorReporter.captureException(error);
          throw new Error(
            `Broken image upload for image with compId ${compId}`,
          );
        }

        eventsHanlder?.({
          type: 'onSingleUploadSuccess',
          payload: {
            ...itemEventPayload,
            imageUrl: staticUri,
          },
        });

        return [
          compId,
          {
            ...data,
            staticUri,
          },
        ];
      },
    );

    const uploadedEntries = await Promise.all(uploadPromises);
    const aiMediaData = createAiMediaData(Object.fromEntries(uploadedEntries));
    eventsHanlder?.({ type: 'onBunchUploadSuccess', payload: eventPayload });
    return aiMediaData;
  };

  /**
   * Makes All previously generated images available for reuse
   */
  public async freeImages() {
    this.imagesStorageManager.manageEntities((storage) => storage.freeAll());
  }

  /**
   * Clean's up images from the storages so next generation
   * will be 100% uniq.
   */
  public async flushImages() {
    this.imagesStorageManager.manageEntities((storage, key) => {
      storage.flush(() => {});
      this.imagesStorageManager.removeStorage(key);
    });
  }

  public async clearUriData() {
    this.uriData.clear();
  }
}
